import { action, observable } from 'mobx';
import { WarehouseEditForm } from 'modules/warehouse/forms';
import { BaseViewStore } from 'core/stores';
import { ApplicationStore } from 'platform/application/stores';
import { CoreUserService, FormHelperService } from 'common/data';
import { formInvalidate, isSome, generateRandomString } from 'core/utils';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, organizationTypes, sourceOfChange, warehouseManagementType } from 'common/constants';
import _ from 'lodash';
import BaasicDropdownViewStore from 'common/stores/BaasicDropdownViewStore';

class WarehouseEditViewStore extends BaseViewStore {
	isUpdateAction = isSome(this.rootStore.routerStore.routerState.params.id);
	organizationTypeId = null;
	@observable timezoneOptions = [];
	@observable tempTimezoneOptions = [];
	@observable pageTimezone = 1;
	@observable stateOptions = [];
	@observable tempStateOptions = [];
	@observable pageState = 1;
	@observable countryOptions = [];
	@observable tempCountryOptions = [];
	@observable pageCountry = 1;
	@observable infiniteSearchPhrase = null;
	@observable isTimezoneLoading = false;
	@observable isStateLoading = false;
	@observable isCountryLoading = false;
	@observable isHavingAPIKey = false;
	debouncedTimezoneSearch = null;
	debouncedStateSearch = null;
	debouncedCountrySearch = null;

	form = new WarehouseEditForm({
		onSuccess: async () => {
			this.createOrUpdateApplication();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		this.listStore = new ApplicationStore(this.rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.coreUserService = new CoreUserService(rootStore.app.baasic.apiClient);
		this.formHelperService = new FormHelperService();
		this.warehouseManagementTypeDropdownStore = new BaasicDropdownViewStore({
			store: new LookupStore(this.rootStore),
			field: this.form.$('warehouseOrganizationInfo.warehouseManagementType'),
			itemTemplate: {
				value: 'id',
				label: 'name',
			},
			lookupRoute: lookupRouteName.warehouseManagementType,
			onChangeCallback: value => {
				this.setWarehouseManagementRules(value);
				this.clearWmsFields(value);
			},
		});
		this.initialize();
	}

	@action
	initialize = async () => {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.form.reset();
		await this.getLookupOptions(['timezone', 'state', 'country']);
		this.debouncedTimezoneSearch = _.debounce((searchPhrase, { action }) => {
			this.searchTimezoneAsync(searchPhrase, { action });
		}, 500);
		this.debouncedStateSearch = _.debounce((searchPhrase, { action }) => {
			this.searchStateAsync(searchPhrase, { action });
		}, 500);
		this.debouncedCountrySearch = _.debounce((searchPhrase, { action }) => {
			this.searchCountryAsync(searchPhrase, { action });
		}, 500);
		if (this.isUpdateAction) {
			await this.getApplication(this.rootStore.routerStore.routerState.params.id);

			if (this.listStore.item.apiKey != '') this.isHavingAPIKey = true;
			const additionalInfo = this.listStore.item.warehouseOrganizationInfo;
			this.listStore.item.address = additionalInfo.address;
			this.listStore.item.contact = additionalInfo.contact;

			this.formHelperService.updateForm(this.form, this.listStore.item, true);

			if (additionalInfo.warehouseManagementType) {
				this.form.$('warehouseOrganizationInfo.warehouseManagementType').value = {
					value: additionalInfo.warehouseManagementType.id,
					label: additionalInfo.warehouseManagementType.name,
					extra: additionalInfo.warehouseManagementType,
				};
				this.warehouseManagementTypeDropdownStore.setValue(
					this.form.$('warehouseOrganizationInfo.warehouseManagementType').value
				);
				this.setWarehouseManagementRules(this.warehouseManagementTypeDropdownStore.value);
			}
			this.form.$('enableEmailNotification').value = additionalInfo.enableEmailNotification;
			this.form.$('cutoffTime').value = additionalInfo.cutoffTime;
			this.form.$('acceptingReturns').value = additionalInfo.acceptingReturns;
			this.form.$('warehouseStatus').value = {
				value: additionalInfo.warehouseStatus.id,
				label: additionalInfo.warehouseStatus.name,
				extra: additionalInfo.warehouseStatus,
			};
			this.form.$('inventoryLevel').value = {
				value: additionalInfo.inventoryLevel,
				label: additionalInfo.inventoryLevel,
			};
			if (additionalInfo && additionalInfo.address && additionalInfo.address.coreCountry) {
				this.form.$('country.id').value = {
					value: additionalInfo.address.coreCountryId,
					label: additionalInfo.address.coreCountry.name,
				};
			}
			if (additionalInfo && additionalInfo.address && additionalInfo.address.coreCountryState) {
				this.form.$('state.id').value = {
					value: additionalInfo.address.coreCountryStateId,
					label: additionalInfo.address.coreCountryState.name,
				};
			}
			if (additionalInfo && additionalInfo.timezone) {
				this.form.$('timezone').value = {
					value: additionalInfo.timezoneId,
					label: additionalInfo.timezone.name,
					extra: additionalInfo.timezone,
				};
			}
		} else {
			this.form.$('enableEmailNotification').value = false;
			this.form.$('acceptingReturns').value = false;
			this.form.$('email').set('rules', 'required|email|string');
			this.form.$('password').set('rules', 'required|string|password');
			this.form.$('confirmPassword').set('rules', 'required|string|same:password');
		}
		this.setOrganizationTypeId();
		this.resume();
	};

	@action.bound
	clearWmsFields = value => {
		if (value) {
			if (value.extra.abrv === warehouseManagementType.daVinci) {
				this.form.$('warehouseOrganizationInfo.facilityId').clear();
				this.form.$('warehouseOrganizationInfo.userLoginId').clear();
			}
			if (value.extra.abrv === warehouseManagementType.extensiv) {
				this.form.$('warehouseOrganizationInfo.warehouseId').clear();
			}
		} else {
			this.form.$('warehouseOrganizationInfo').clear();
		}
	};

	@action.bound
	setWarehouseManagementRules = value => {
		if (value) {
			if (value.extra.abrv === warehouseManagementType.daVinci) {
				this.form.$('warehouseOrganizationInfo.warehouseId').set('rules', 'required');
			} else {
				this.form.$('warehouseOrganizationInfo.warehouseId').set('rules', '');
			}
			if (value.extra.abrv === warehouseManagementType.extensiv) {
				this.form.$('warehouseOrganizationInfo.facilityId').set('rules', 'required');
				this.form.$('warehouseOrganizationInfo.userLoginId').set('rules', 'required');
			} else {
				this.form.$('warehouseOrganizationInfo.facilityId').set('rules', '');
				this.form.$('warehouseOrganizationInfo.userLoginId').set('rules', '');
			}
		} else {
			this.form.$('warehouseOrganizationInfo.warehouseId').set('rules', '');
			this.form.$('warehouseOrganizationInfo.facilityId').set('rules', '');
			this.form.$('warehouseOrganizationInfo.userLoginId').set('rules', '');
		}
	};

	@action.bound async setOrganizationTypeId() {
		await this.lookupStore.find({ page: 1, rpp: 250 }, lookupRouteName.organizationType);
		this.organizationTypeId = _.find(this.lookupStore.data, organizationType => {
			return organizationType.abrv.toUpperCase() == organizationTypes.warehouse.toUpperCase();
		}).id;
	}

	@action.bound
	getLookupOptions = async lookups => {
		for await (var lookup of lookups) {
			await this.lookupStore.find({ page: 1, rpp: 250 }, lookupRouteName[lookup]);
			this[`${lookup}Options`] = _.map(this.lookupStore.data, item => {
				return { label: item.currency != null ? item.currency : item.name, value: item.id, extra: item };
			});
		}
	};

	@action async getApplication(id) {
		await this.listStore.getApplication(id, {
			embed: [
				'warehouseOrganizationInfo',
				'warehouseOrganizationInfo.warehouseStatus',
				'warehouseOrganizationInfo.address',
				'warehouseOrganizationInfo.address.coreCountry',
				'warehouseOrganizationInfo.address.coreCountryState',
				'warehouseOrganizationInfo.contact',
				'warehouseOrganizationInfo.timezone',
				'warehouseOrganizationInfo.warehouseManagementType',
			],
		});
	}

	@action.bound
	async handleOnTimezoneMenuScroll() {
		if (this.timezoneOptions.length < this.pageTimezone * 15) return;
		this.isTimezoneLoading = true;
		this.pageTimezone++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.pageTimezone, rpp: 15 },
			lookupRouteName.timezone
		);
		this.tempTimezoneOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempTimezoneOptions.length > 0) {
			this.timezoneOptions = this.timezoneOptions.concat(this.tempTimezoneOptions);
		}
		this.isTimezoneLoading = false;
	}

	@action.bound
	async handleOnStateMenuScroll() {
		if (this.stateOptions.length < this.pageState * 15) return;
		this.isStateLoading = true;
		this.pageState++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.pageState, rpp: 15 },
			lookupRouteName.state
		);
		this.tempStateOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempStateOptions.length > 0) {
			this.stateOptions = this.stateOptions.concat(this.tempStateOptions);
		}
		this.isStateLoading = false;
	}

	@action.bound
	async handleOnCountryMenuScroll() {
		if (this.countryOptions.length < this.pageCountry * 15) return;
		this.isCountryLoading = true;
		this.pageCountry++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.pageCountry, rpp: 15 },
			lookupRouteName.country
		);
		this.tempCountryOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempCountryOptions.length > 0) {
			this.countryOptions = this.countryOptions.concat(this.tempCountryOptions);
		}
		this.isCountryLoading = false;
	}

	@action.bound
	async onMenuClose(routeName) {
		this.pageTimezone = 1;
		this.pageState = 1;
		this.pageCountry = 1;
		this.infiniteSearchPhrase = null;
		await this.getLookupOptions(routeName);
	}

	@action.bound
	async searchTimezoneAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageTimezone = 1;
		}
		if (action === 'input-change') {
			this.isTimezoneLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageTimezone = 1;
			this.timezoneOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.timezone);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.timezoneOptions = newOptions;
			this.isTimezoneLoading = false;
		}
	}

	@action.bound
	async searchStateAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageState = 1;
		}
		if (action === 'input-change') {
			this.isStateLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageState = 1;
			this.stateOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.state);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.stateOptions = newOptions;
			this.isStateLoading = false;
		}
	}

	@action.bound
	async searchCountryAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageCountry = 1;
		}
		if (action === 'input-change') {
			this.isCountryLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageCountry = 1;
			this.countryOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.country);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.countryOptions = newOptions;
			this.isCountryLoading = false;
		}
	}

	@action.bound
	onSelectChange = (selectedOption, field) => {
		field.value = selectedOption;
	};

	@action.bound onTimeChange(time, field) {
		if (time && time.value && field) {
			this.form.$(field).value = time.formattedValue;
		}
	}

	@action.bound generateApiKey() {
		this.isHavingAPIKey = true;
		var radnomStringGenerated = generateRandomString(32);
		this.form.$('apiKey').set('value', radnomStringGenerated);
	}

	@action.bound
	onCheck(event, path) {
		this.form.$(path).value = event.currentTarget.checked;
	}

	@action async createOrUpdateApplication() {
		this.suspend();
		const {
			identifier,
			applicationTitle,
			address: { address1, address2, address3, city, zipCode },
			country,
			state,
			timezone,
			contact,
			warehouseOrganizationInfo,
			warehouseStatus,
			cutoffTime,
			acceptingReturns,
			email,
			password,
			confirmPassword,
			apiKey,
			enableEmailNotification,
			inventoryLevel,
		} = this.form.values();

		let warehouseManagementCredentials = {};
		if (warehouseOrganizationInfo.warehouseManagementType) {
			if (warehouseOrganizationInfo.warehouseManagementType.extra.abrv === warehouseManagementType.daVinci) {
				warehouseManagementCredentials = {
					warehouseId: warehouseOrganizationInfo.warehouseId,
				};
			} else if (warehouseOrganizationInfo.warehouseManagementType.extra.abrv === warehouseManagementType.extensiv) {
				warehouseManagementCredentials = {
					facilityId: warehouseOrganizationInfo.facilityId,
					userLoginId: warehouseOrganizationInfo.userLoginId,
				};
			}
		}

		let addressObj = {
			address1,
			address2,
			address3,
			city,
			zipCode,
			coreCountryStateId: state.id.value ? state.id.value : state.id,
			coreCountryId: country.id.value ? country.id.value : country.id,
		};
		var organization = {
			identifier,
			applicationTitle,
			organizationTypeId: this.organizationTypeId,
			apiKey,
			isActive: warehouseStatus.extra.abrv === 'open' ? true : false,
		};
		var organizationInfo = {
			...warehouseManagementCredentials,
			address: addressObj,
			contact,
			timezoneId: timezone.value ? timezone.value : timezone,
		};

		if (!this.isUpdateAction) {
			organization = {
				...organization,
				membershipData: {
					email,
					password,
				},

				coreUserInfo: {
					piiAccess: true,
				},

				confirmPassword,
			};
		} else {
			const additionalInfo = this.listStore.item.warehouseOrganizationInfo;
			organizationInfo = {
				...warehouseManagementCredentials,
				isAvailable: additionalInfo.isAvailable,
				useAutoExitScan: additionalInfo.useAutoExitScan,
				useRegularPalletizingMode: additionalInfo.useRegularPalletizingMode,
				id: additionalInfo.id,
				addressId: additionalInfo.address.id,
				contactId: additionalInfo.contact.id,
				address: addressObj,
				contact: contact,
				timezoneId: timezone.value,
				timezone: timezone.extra,
			};
		}
		organization.warehouseOrganizationInfo = {
			...organizationInfo,
			enableEmailNotification: enableEmailNotification,
			cutoffTime,
			acceptingReturns: acceptingReturns,
			inventoryLevel: inventoryLevel.value,
			warehouseStatusId: warehouseStatus.value,
			warehouseStatus: warehouseStatus.extra,
			warehouseManagementTypeId: warehouseOrganizationInfo.warehouseManagementType
				? warehouseOrganizationInfo.warehouseManagementType.value
				: null,
		};
		if (
			!this.isUpdateAction &&
			password
				.trim()
				.toLowerCase()
				.includes(
					email
						.split('@')[0]
						.toLowerCase()
						.trim()
				)
		) {
			this.form.invalidate('PASSWORDCONTAINSUNSERNAME');
		} else {
			try {
				this.isUpdateAction
					? await this.listStore.updateApplication(
							{
								id: this.rootStore.routerStore.routerState.params.id,
								...organization,
							},
							sourceOfChange.editWarehouse
					  )
					: await this.listStore.createApplication(organization, sourceOfChange.createWarehouse);
				this.rootStore.notificationStore.success(
					this.isUpdateAction
						? ['LOOKUPUPDATE.WAREHOUSE', { name: organization.identifier }]
						: ['LOOKUPCREATE.WAREHOUSE', { name: organization.identifier }]
				);

				if (!this.isUpdateAction) {
					this.rootStore.notificationStore.warning(['WAREHOUSEVIEW.ADDSELLERWAREHOUSES']);
				}
				this.rootStore.routerStore.goTo('master.app.main.warehouse.list');
			} catch (err) {
				formInvalidate({ form: this.form, err });
			}
		}
		this.resume();
	}
}

export default WarehouseEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/warehouse/stores/WarehouseEditViewStore.js